// Notifications bell (desktop header and the companion header). Gathers
// the operator actions waiting across the workspace (an agent's Google
// token expired, skills to review, a connected session blocked, …) into
// one list of one-click actions.
//
// Desktop: a dropdown under the bell, portaled to <body> because the
// shell's backdrop-filter surfaces would trap its position:fixed; opaque
// overlay surface, since Earth and Midnight turn blur off. Phones: the
// same list in a bottom sheet (a 380px popover would overflow).

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Bell } from "lucide-react";

import { useNotifications } from "@/api/notifications";
import { Button } from "@/components/ui/Button";
import { OVERLAY_SHADOW, Z, cx } from "@/components/ui/controlStyles";
import { isTopLayer, useAnchoredPosition, useFocusTrap } from "@/components/ui/overlayHooks";
import { Sheet } from "@/components/ui/Sheet";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { pluralize } from "@/lib/format";

import { NotificationList } from "./NotificationList";

interface NotificationsBellProps {
  customerId: string | null;
}

export function NotificationsBell({ customerId }: NotificationsBellProps) {
  const q = useNotifications(customerId);
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const isMobile = useIsMobile();

  // Opening another page (usually through a row's action) closes it.
  const location = useLocation();
  useEffect(() => setOpen(false), [location.pathname]);

  // The Google OAuth callback page posts `oc:google_reconnected` back to
  // this window when a Reconnect finishes in the popup tab: refetch now
  // rather than showing the stale warning until the next 60 s poll, and
  // nudge the caches the agent pages read.
  useEffect(() => {
    function onMsg(e: MessageEvent) {
      if (e.origin !== window.location.origin) return;
      const data = e.data;
      if (!data || typeof data !== "object") return;
      if ((data as { type?: unknown }).type !== "oc:google_reconnected") return;
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      queryClient.invalidateQueries({ queryKey: ["google-status"] });
      queryClient.invalidateQueries({ queryKey: ["mailbox"] });
    }
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [queryClient]);

  const items = q.data?.items ?? [];
  const count = items.length;
  const urgent = items.some((i) => i.severity === "urgent");
  const close = () => setOpen(false);

  return (
    <>
      <Button
        ref={anchorRef}
        variant="ghost"
        // 40px with a 20px glyph on a phone, matching the companion's
        // avatar button and tab icons; 32px / 16px in the desktop bar.
        size={isMobile ? "icon-lg" : "icon"}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={count === 0 ? "Notifications, none" : `Notifications, ${count} waiting`}
        // Open reads as the hover surface; aria-expanded carries the state
        // (no aria-pressed: a popup button is not a toggle).
        className={cx("shrink-0", open && "bg-raised text-fg")}
      >
        <Bell aria-hidden className={isMobile ? "h-5 w-5" : "h-4 w-4"} strokeWidth={1.75} />
        {count > 0 && (
          <span
            aria-hidden
            className={cx(
              // Centred just outside the glyph's top-right corner, so it
              // reads as the bell's and leaves its outline visible. On a
              // phone it stays inside the 40px button: the companion bar
              // puts the button 2px from the top of the screen.
              "absolute grid h-[18px] min-w-[18px] place-items-center rounded-pill px-1 text-xs font-semibold leading-none tabular-nums",
              isMobile ? "right-0 top-[2px]" : "-right-1 -top-1",
              "shadow-[0_0_0_2px_var(--c-bg)]",
              // A solid fill so it reads on every bar: amber for things
              // waiting, red only when something is urgent. Not the accent:
              // Earth's terracotta is too close to its danger red.
              urgent ? "bg-danger-fill text-on-danger" : "bg-warning text-surface-950",
            )}
          >
            {count > 9 ? "9+" : count}
          </span>
        )}
      </Button>

      {open && isMobile && (
        <Sheet
          open
          onClose={close}
          title="Notifications"
          description={count > 0 ? `${pluralize(count, "thing")} waiting for you` : undefined}
          contentClassName="pb-2"
        >
          <NotificationList items={items} loading={q.isLoading} error={q.isError && !q.data} onRetry={() => void q.refetch()} onClose={close} />
        </Sheet>
      )}
      {open &&
        !isMobile &&
        createPortal(
          <NotificationsPopover anchorRef={anchorRef} count={count} loading={q.isLoading} onClose={close}>
            <NotificationList items={items} loading={q.isLoading} error={q.isError && !q.data} onRetry={() => void q.refetch()} onClose={close} />
          </NotificationsPopover>,
          document.body,
        )}
    </>
  );
}

interface PopoverProps {
  anchorRef: React.RefObject<HTMLButtonElement>;
  count: number;
  loading: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

// Right-aligned under the bell, 380px, kept inside the viewport.
function NotificationsPopover({ anchorRef, count, loading, onClose, children }: PopoverProps) {
  const panel = useRef<HTMLDivElement>(null);
  const pos = useAnchoredPosition(anchorRef, panel, true, { align: "end", matchWidth: false, minWidth: 380, offset: 8 });
  // Focus moves into the panel and back to the bell when it closes.
  useFocusTrap(panel, true);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape" || e.defaultPrevented || !isTopLayer(panel.current)) return;
      e.preventDefault();
      onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0" style={{ zIndex: Z.dropdown }} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        ref={panel}
        role="dialog"
        aria-label="Notifications"
        tabIndex={-1}
        className={cx(
          "fixed flex w-[380px] max-w-[calc(100vw-16px)] flex-col overflow-hidden rounded-card border border-line bg-overlay outline-none",
          OVERLAY_SHADOW,
        )}
        style={{
          top: pos?.top ?? 64,
          left: pos?.left,
          right: pos ? undefined : 16,
          maxHeight: Math.min(pos?.maxHeight ?? 560, 560),
          visibility: pos ? "visible" : "hidden",
          animation: "fadeIn 120ms ease-out both",
        }}
      >
        <div className="flex shrink-0 items-baseline justify-between gap-3 border-b border-line-subtle px-4 py-3">
          <h2 className="text-base font-semibold text-fg">Notifications</h2>
          <span className="text-sm text-fg-muted">{loading ? "Checking…" : count > 0 ? `${count} waiting` : ""}</span>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}
